import React, {FC, ReactNode} from "react";
import {Radio} from "antd";
import {blue} from "@ant-design/colors";
import i18n from "../../../services/i18n";

/**
 * Eine Option in einem Wizard-Schritt (#662).
 *
 * Die Schritte Preis, alte Buchung und Guthaben sind Optionslisten: die Folge-Tickets
 * (#663 anteilig/manuell und besuchte Lektionen, #665 Mollie-Refund/manuelle Erstattung)
 * stecken weitere Einträge in dieselbe Liste, ohne den Schritt umzubauen. `extra` ist
 * der Platz für optionsspezifische Eingaben (Stichtag, Betrag, Grund), die nur bei
 * gewählter Option sichtbar sind.
 */
export type TransferOption = {
    key: string;
    title: ReactNode;
    description?: ReactNode;
    disabled?: boolean;
    /** Zusatzinhalt, nur sichtbar wenn die Option gewählt ist. */
    extra?: ReactNode;
    /** Vorbelegter Vorschlag — bekommt in der Kartenansicht die Marke „Vorschlag". */
    recommended?: boolean;
};

export type TransferOptionLayout = "list" | "cards";

type TransferOptionListProps = {
    options: Array<TransferOption>;
    value?: string;
    onChange: (key: string) => void;
    /** Für Tests und Barrierefreiheit: Gruppenname der Radios. */
    name?: string;
    /** `cards`: Optionen nebeneinander als Karten (Entscheidungen im Kopf der Zusammenfassung). */
    layout?: TransferOptionLayout;
};

const CARD_LINE = "#d9d9d9";

const TransferOptionList: FC<TransferOptionListProps> = ({options, value, onChange, name, layout = "list"}) => {
    const tr = i18n.containers.bookings.TransferWizard.summary;
    const badge = <span style={{fontSize: 11, color: "#389e0d", border: "1px solid #b7eb8f", background: "#f6ffed", borderRadius: 10, padding: "0 6px", lineHeight: "16px", whiteSpace: "nowrap"}}>{tr.recommended()}</span>;

    if (layout === "cards") {
        return <Radio.Group name={name} value={value} onChange={(event) => onChange(event.target.value)} style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, width: "100%"}}>
            {options.map((option) => {
                const selected = value === option.key;

                return <div key={option.key} data-option-card={option.key} style={{border: `1px solid ${selected ? blue[5] : CARD_LINE}`, boxShadow: selected ? `0 0 0 1px ${blue[5]}` : undefined, borderRadius: 8, padding: "10px 14px", background: "#fff", opacity: option.disabled ? 0.6 : 1}}>
                    <Radio value={option.key} disabled={option.disabled} style={{display: "block", whiteSpace: "normal", width: "100%"}}>
                        <span style={{display: "flex", alignItems: "center", gap: 8}}>
                            <span style={{fontWeight: 600, flex: 1}}>{option.title}</span>
                            {option.recommended && badge}
                        </span>
                        {option.description && <div style={{color: "rgba(0,0,0,0.65)", fontWeight: 400, fontSize: 13, marginTop: 2}}>{option.description}</div>}
                    </Radio>
                    {selected && option.extra && <div style={{marginLeft: 24, marginTop: 8}}>{option.extra}</div>}
                </div>;
            })}
        </Radio.Group>;
    }

    return <Radio.Group name={name} value={value} onChange={(event) => onChange(event.target.value)} style={{display: "block"}}>
        {options.map((option) => (
            <div key={option.key} data-option-card={option.key} style={{padding: "8px 0", borderBottom: "1px solid #f0f0f0"}}>
                <Radio value={option.key} disabled={option.disabled} style={{display: "block", whiteSpace: "normal"}}>
                    <span style={{fontWeight: 500}}>{option.title}</span>
                    {option.recommended && <span style={{marginLeft: 8}}>{badge}</span>}
                    {option.description && <div style={{color: "rgba(0,0,0,0.65)", fontWeight: 400}}>{option.description}</div>}
                </Radio>
                {value === option.key && option.extra && <div style={{marginLeft: 24, marginTop: 8}}>{option.extra}</div>}
            </div>
        ))}
    </Radio.Group>;
};

export default TransferOptionList;
